import { useEffect, useState } from 'react'
import { Upload, Database, AlertCircle, CheckCircle, RefreshCw, Search } from 'lucide-react'
import { gesnAPI } from '../../services/api'
import { Button } from '../Common/Button'
import toast from 'react-hot-toast'

export const GesnUpload = () => {
  const [bases, setBases] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [hits, setHits] = useState([])

  const loadBases = async () => {
    setLoading(true)
    try {
      const response = await gesnAPI.listBases()
      setBases(response.data || [])
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось загрузить список баз ГЭСН')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadBases()
  }, [])

  const inflight = bases.some((b) => b.status === 'pending' || b.status === 'processing')
  useEffect(() => {
    if (!inflight) return undefined
    const timer = setInterval(loadBases, 4000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inflight])

  const handleFile = async (file) => {
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.xml')) {
      toast.error('Требуется XML-файл ФСНБ/ГЭСН')
      return
    }
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await gesnAPI.upload(formData)
      toast.success(response.data?.message || 'Файл принят, загрузка в фоне')
      await loadBases()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка загрузки XML')
    } finally {
      setUploading(false)
    }
  }

  const handleSearch = async (e) => {
    e?.preventDefault()
    if (!query.trim()) return
    setSearching(true)
    try {
      const response = await gesnAPI.search(query.trim(), { limit: 20 })
      setHits(response.data?.items || [])
      if (!(response.data?.items || []).length) {
        toast('Ничего не найдено в базе ГЭСН', { icon: '🔍' })
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка поиска')
    } finally {
      setSearching(false)
    }
  }

  const statusBadge = (status) => {
    const map = {
      ready: 'bg-green-100 text-green-700',
      processing: 'bg-blue-100 text-blue-700',
      pending: 'bg-yellow-100 text-yellow-700',
      error: 'bg-red-100 text-red-700',
    }
    const labels = {
      ready: 'Готово',
      processing: 'Загрузка',
      pending: 'В очереди',
      error: 'Ошибка',
    }
    return (
      <span className={`px-2 py-0.5 text-xs rounded ${map[status] || 'bg-gray-100 text-gray-600'}`}>
        {labels[status] || status}
      </span>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-3xl">📘</span>
          <div>
            <h3 className="text-xl font-semibold">Нормативная база ГЭСН (ФСНБ)</h3>
            <p className="text-sm text-gray-600">
              Загрузите XML сборника ГЭСН. Расчёт ЛСР использует только эту базу — ИИ не придумывает коды.
            </p>
          </div>
        </div>

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
          }`}
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true) }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
          onDragLeave={(e) => { e.preventDefault(); setDragActive(false) }}
          onDrop={(e) => {
            e.preventDefault()
            setDragActive(false)
            const file = e.dataTransfer.files?.[0]
            if (file) handleFile(file)
          }}
        >
          <Upload className="mx-auto text-gray-400 mb-3" size={36} />
          <p className="text-gray-700 mb-2">Перетащите XML ФСНБ/ГЭСН или выберите файл</p>
          <p className="text-xs text-gray-500 mb-4">Ожидаемый размер — десятки/сотни МБ, загрузка идёт в фоне</p>
          <label className="inline-block">
            <input
              type="file"
              accept=".xml"
              className="hidden"
              disabled={uploading}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <span className={`px-4 py-2 rounded-lg text-white text-sm cursor-pointer ${
              uploading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
            }`}>
              {uploading ? 'Загрузка…' : 'Выбрать XML'}
            </span>
          </label>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold flex items-center gap-2">
            <Database size={16} /> Загруженные редакции
          </h4>
          <Button variant="ghost" size="sm" onClick={loadBases} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
        {bases.length === 0 ? (
          <p className="text-sm text-gray-500">База ещё не загружена</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-2 px-3">Редакция</th>
                  <th className="text-left py-2 px-3">Уровень цен</th>
                  <th className="text-center py-2 px-3">Норм</th>
                  <th className="text-center py-2 px-3">Ресурсов</th>
                  <th className="text-center py-2 px-3">Статус</th>
                </tr>
              </thead>
              <tbody>
                {bases.map((b) => (
                  <tr key={b.id} className="border-b">
                    <td className="py-2 px-3">
                      <div className="font-medium">{b.base_version || b.base_name}</div>
                      <div className="text-xs text-gray-500">{b.source_filename}</div>
                      {b.error_message && (
                        <div className="text-xs text-red-600 flex items-center gap-1 mt-1">
                          <AlertCircle size={12} /> {b.error_message}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3">{b.price_level || '—'}</td>
                    <td className="text-center py-2 px-3">{b.norms_count}</td>
                    <td className="text-center py-2 px-3">{b.resources_count}</td>
                    <td className="text-center py-2 px-3">{statusBadge(b.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <Search size={16} /> Поиск расценок
        </h4>
        <form onSubmit={handleSearch} className="flex gap-2 mb-4">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Например: огнезащита металлических поверхностей"
            className="flex-1 px-3 py-2 border rounded-lg text-sm"
          />
          <button
            type="submit"
            disabled={searching}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {searching ? 'Поиск…' : 'Найти'}
          </button>
        </form>
        {hits.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-2 px-3">Код</th>
                  <th className="text-left py-2 px-3">Наименование</th>
                  <th className="text-center py-2 px-3">Измеритель</th>
                  <th className="text-center py-2 px-3">Сборник</th>
                </tr>
              </thead>
              <tbody>
                {hits.map((n) => (
                  <tr key={n.id} className="border-b">
                    <td className="py-2 px-3 font-mono text-xs">{n.code}</td>
                    <td className="py-2 px-3">{n.name_full}</td>
                    <td className="text-center py-2 px-3">{n.unit_raw}</td>
                    <td className="text-center py-2 px-3">{n.collection}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {hits.length === 0 && query && !searching && (
          <p className="text-sm text-gray-500 flex items-center gap-1">
            <CheckCircle size={14} className="text-gray-400" /> Нет результатов
          </p>
        )}
      </div>
    </div>
  )
}

export default GesnUpload
